import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface BundleComponent {
  id: number;
  bundle_id: number;
  product_id: number;
  quantity: number;
  notes?: string;
  product_sku?: string;
  product_name?: string;
  product_price?: number;
}

interface BundleAvailability {
  bundle_id: number;
  bundle_sku: string;
  bundle_name: string;
  available_kits: number;
  components: Array<{
    product_id: number;
    sku: string;
    name: string;
    quantity_required: number;
    stock_available: number;
    kits_possible: number;
    is_bottleneck: boolean;
    unit_price: number;
    line_value: number;
  }>;
  total_component_value: number;
}

interface Props {
  productId: number;
  productType?: string;
}

const BundleComponentManager: React.FC<Props> = ({ productId, productType }) => {
  const [components, setComponents] = useState<BundleComponent[]>([]);
  const [availability, setAvailability] = useState<BundleAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Array<{id: number; sku: string; name: string; price?: number; attributes?: Array<{attribute_name: string; value: string}>}>>([]);

  // Helper: format product label as "SKU - Name - Color - Decoration"
  const getProductLabel = (product: {sku: string; name: string; attributes?: Array<{attribute_name: string; value: string}>}) => {
    const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
    const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
    let label = `${product.sku} - ${product.name}`;
    if (color) label += ` - ${color}`;
    if (decoration) label += ` - ${decoration}`;
    return label;
  };
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  useEffect(() => {
    if (productType === 'bundle') {
      fetchComponents();
      fetchAvailability();
    }
  }, [productId, productType]);

  const fetchComponents = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/products/${productId}/components`);
      setComponents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const res = await api.get(`/products/${productId}/availability`);
      setAvailability(res.data);
    } catch (e) {
      setAvailability(null);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products/', { params: { limit: 500 } });
      setProducts((res.data.products || []).filter((p: any) => p.id !== productId));
    } catch (e) {}
  };

  const handleAdd = async () => {
    if (!selectedProductId || quantity < 1) return;
    setSaving(true);
    try {
      await api.post(`/products/${productId}/components`, {
        product_id: selectedProductId,
        quantity,
      });
      setShowAddForm(false);
      setSearchText('');
      setSelectedProductId(null);
      setQuantity(1);
      fetchComponents();
      fetchAvailability();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to add component');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (componentId: number) => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/products/${productId}/components/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchComponents();
      fetchAvailability();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleRemoveClick = (componentId: number, name: string) => {
    setDeleteConfirm({show: true, id: componentId, name});
  };

  const handleUpdateQty = async (componentId: number, productIdComp: number, newQty: number) => {
    if (newQty < 1) return;
    try {
      await api.put(`/products/${productId}/components/${componentId}`, {
        product_id: productIdComp,
        quantity: newQty,
      });
      fetchComponents();
      fetchAvailability();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update');
    }
  };

  if (productType !== 'bundle') return null;

  const filteredProducts = products.filter(p => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
  }).slice(0, 15);

  const totalValue = components.reduce((sum, c) => sum + (c.product_price || 0) * c.quantity, 0);

  return (
    <div style={{ marginTop: '24px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Bundle Components (BOM)</h4>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            {components.length} component{components.length !== 1 ? 's' : ''} | Total value: ₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {availability && (
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600,
              background: availability.available_kits > 0 ? '#dcfce7' : '#fef2f2',
              color: availability.available_kits > 0 ? '#16a34a' : '#dc2626',
            }}>
              {availability.available_kits} kits available
            </span>
          )}
          <button
            className="btn btn-primary"
            style={{ fontSize: '12px', padding: '5px 12px' }}
            onClick={() => { setShowAddForm(true); fetchProducts(); }}
          >
            + Add Component
          </button>
        </div>
      </div>

      {/* Components List */}
      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
      ) : components.length === 0 ? (
        <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '14px', marginBottom: '4px' }}>No components added yet</div>
          <div style={{ fontSize: '12px' }}>Add products that make up this bundle/kit</div>
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>SKU</th>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Product</th>
              <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Qty</th>
              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Unit Price</th>
              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Line Value</th>
              <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Stock</th>
              <th style={{ padding: '8px 16px', width: '60px' }}></th>
            </tr>
          </thead>
          <tbody>
            {components.map(comp => {
              const avComp = availability?.components.find(c => c.product_id === comp.product_id);
              return (
                <tr key={comp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>{comp.product_sku}</td>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>{comp.product_name}</td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <input
                      type="number"
                      min={1}
                      value={comp.quantity}
                      onChange={(e) => handleUpdateQty(comp.id, comp.product_id, parseInt(e.target.value) || 1)}
                      style={{ width: '50px', textAlign: 'center', padding: '3px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }}
                    />
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', color: '#475569' }}>
                    {comp.product_price ? `₹${comp.product_price.toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>
                    {comp.product_price ? `₹${(comp.product_price * comp.quantity).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    {avComp ? (
                      <span style={{
                        color: avComp.is_bottleneck ? '#dc2626' : '#16a34a',
                        fontWeight: avComp.is_bottleneck ? 600 : 400,
                      }}>
                        {avComp.stock_available}
                        {avComp.is_bottleneck && ' ⚠️'}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleRemoveClick(comp.id, comp.product_sku || `Component #${comp.id}`)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '14px' }}
                      title="Remove component"
                    >×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add Component Form */}
      {showAddForm && (
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ flex: 2, position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>Product</label>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Type SKU or product name..."
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '13px' }}
              />
              {searchText.trim() && filteredProducts.length > 0 && !selectedProductId && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '160px', overflow: 'auto' }}>
                  {filteredProducts.map(p => (
                    <div
                      key={p.id}
                      onClick={() => { setSelectedProductId(p.id); setSearchText(getProductLabel(p)); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{p.sku}</span> - {p.name}
                      {p.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value && (
                        <span style={{ color: '#64748b' }}> - {p.attributes.find(a => a.attribute_name.toLowerCase() === 'color')!.value}</span>
                      )}
                      {p.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value && (
                        <span style={{ color: '#64748b' }}> - {p.attributes.find(a => a.attribute_name.toLowerCase() === 'decoration')!.value}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ width: '80px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>Qty</label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '13px', textAlign: 'center' }}
              />
            </div>
            <button className="btn btn-primary" style={{ fontSize: '12px', padding: '7px 14px' }} onClick={handleAdd} disabled={!selectedProductId || saving}>
              {saving ? '...' : 'Add'}
            </button>
            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '7px 14px' }} onClick={() => { setShowAddForm(false); setSearchText(''); setSelectedProductId(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Remove Component"
        message="Remove this component from the bundle?"
        itemName={deleteConfirm.name}
        onConfirm={handleRemove as any}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default BundleComponentManager;
